import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import Box from '../../components/Box/Box';
import { theme } from '../../../config/theme';
import { rem, stripUnit } from 'polished';
import {
    Body1,
    Body2,
    Caption,
    Headline,
} from '../../components/Typography/Typography';
import { css } from 'styled-components';
import PrismicLink from '../../components/PrismicLink/PrismicLink';
import CollapseContainer from '../../components/CollapseContainer/CollapseContainer';
import { FOOTER_QUERY } from './__generated__/FOOTER_QUERY';
import { useLang } from '../../hooks/useLang';
import Image from '../Image/Image';
import Grid from '../Grid/Grid';
import Inline from '../Inline/Inline';
import Button from '../Button/Button';
import { KsaFlag } from '../../icons/KsaFlag';

const footerQuery = graphql`
    query FOOTER_QUERY {
        allPrismicFooter {
            nodes {
                lang
                data {
                    link_groups {
                        id
                        document {
                            ... on PrismicLinkGroups {
                                id
                                data {
                                    slices {
                                        ... on PrismicFooterLinkGroupDefault {
                                            id
                                            primary {
                                                group_title
                                            }
                                            items {
                                                name
                                                link {
                                                    ...PRISMIC_LINK_FRAGMENT
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    copyright
                    cta_1_image {
                        alt
                        url
                    }
                    cta_1_link {
                        ...PRISMIC_LINK_FRAGMENT
                    }
                    cta_2_image {
                        url
                        alt
                    }
                    cta_2_link {
                        ...PRISMIC_LINK_FRAGMENT
                    }
                    logo {
                        url
                        alt
                    }
                    backed_by_text
                    backed_by_image {
                        url
                        alt
                    }
                    ksa_button_text
                    ksa_button_link {
                        ...PRISMIC_LINK_FRAGMENT
                    }
                    social_links {
                        icon {
                            url
                            alt
                        }
                        link {
                            ...PRISMIC_LINK_FRAGMENT
                        }
                    }
                    badges_row_1 {
                        img {
                            gatsbyImageData(layout: CONSTRAINED, width: 68)
                            alt
                            url
                        }
                    }
                    badges_row_2 {
                        img {
                            alt
                            url
                        }
                    }
                }
            }
        }
    }
`;

const Footer = () => {
    const lang = useLang();
    const staticData = useStaticQuery(footerQuery);
    const {
        allPrismicFooter: { nodes },
    }: FOOTER_QUERY = staticData;
    const node = nodes.find((node) => node.lang === lang.value);

    if (!node) return null;

    const {
        logo,
        copyright,
        cta_1_image,
        cta_1_link,
        cta_2_image,
        cta_2_link,
        social_links,
        link_groups,
        badges_row_1,
        badges_row_2,
        backed_by_text,
        backed_by_image,
        ksa_button_text,
        ksa_button_link,
    } = node.data;

    const links = link_groups.document.data?.slices;

    return (
        <Box as="footer" flexShrink={0}>
            <Box
                backgroundColor={theme.colors.primary[1800]}
                color={theme.colors.white}
                paddingY={{ _: rem(32), lg: rem(60) }}
            >
                <Grid>
                    <Box
                        gridColumn="1/-1"
                        display="flex"
                        flexDirection="column"
                        gap={{ _: rem(32), lg: rem(60) }}
                    >
                        <Box gridColumn={1}>
                            {/*@ts-ignore */}
                            <Box
                                as={Image}
                                width={{ _: rem(131), md: rem(150) }}
                                img={logo}
                                alt={logo.alt}
                            />

                            <Box
                                display="inline-flex"
                                alignItems="center"
                                gap={rem(6)}
                                mt={rem(16)}
                            >
                                <Body2 bold>{backed_by_text}</Body2>
                                <Box
                                    as={Image}
                                    width="152px"
                                    height="12px"
                                    img={backed_by_image}
                                />
                            </Box>
                        </Box>
                        <Box
                            display={{ _: 'none', md: 'flex' }}
                            gap={rem(32)}
                            flexWrap="wrap"
                            gridColumn={{ md: '1/-1', lg: '2' }}
                        >
                            {links.map(({ primary, items }) => (
                                <Box key={primary.group_title} flex={1}>
                                    <Headline
                                        marginBottom={rem(16)}
                                        fontWeight={500}
                                        color={theme.colors.white}
                                    >
                                        {primary.group_title}
                                    </Headline>
                                    <Box
                                        as="ul"
                                        display="grid"
                                        gridGap={rem(8)}
                                    >
                                        {items.map((item) => (
                                            <Body1
                                                as={PrismicLink}
                                                key={item.name}
                                                link={item.link}
                                                color={
                                                    theme.colors.primary[200]
                                                }
                                                css={css`
                                                    a:hover {
                                                        color: ${theme.colors
                                                            .neutral.white};
                                                        text-decoration: underline;
                                                    }
                                                `}
                                            >
                                                {item.name}
                                            </Body1>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                        <Box as="ul" display={{ md: 'none' }}>
                            {links.map(({ primary, items }, i) => (
                                <CollapseContainer
                                    initiallyOpen={false}
                                    key={i}
                                >
                                    <CollapseContainer.Header>
                                        <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            borderTop="1px solid white"
                                            alignItems="center"
                                            height={rem(56)}
                                            paddingX={rem(8)}
                                        >
                                            <Body1
                                                as="li"
                                                color={theme.colors.white}
                                            >
                                                {primary.group_title}
                                            </Body1>
                                            <CollapseContainer.Arrow
                                                size={32}
                                                color={theme.colors.white}
                                            />
                                        </Box>
                                    </CollapseContainer.Header>
                                    <CollapseContainer.Item>
                                        <Box
                                            css={css`
                                                + * {
                                                    margin-top: ${rem(16)};
                                                }
                                                > :last-of-type {
                                                    margin-bottom: ${rem(24)};
                                                }
                                            `}
                                        >
                                            {items.map(({ link, name }, j) => (
                                                <PrismicLink
                                                    link={link}
                                                    key={j}
                                                    css={css`
                                                        & a {
                                                            padding: ${rem(10)}
                                                                ${rem(16)};
                                                            color: ${({
                                                                theme,
                                                            }) =>
                                                                theme.colors
                                                                    .white};
                                                            display: grid;
                                                            row-gap: ${rem(4)};
                                                        }
                                                        & + * a {
                                                            margin-top: ${rem(
                                                                8
                                                            )};
                                                        }
                                                    `}
                                                >
                                                    <Body2
                                                        color={
                                                            theme.colors
                                                                .primary[300]
                                                        }
                                                    >
                                                        {name}
                                                    </Body2>
                                                </PrismicLink>
                                            ))}
                                        </Box>
                                    </CollapseContainer.Item>
                                </CollapseContainer>
                            ))}
                        </Box>
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap={rem(16)}
                        >
                            {badges_row_1.length && (
                                <Inline
                                    gap={rem(19)}
                                    justifyContent={{
                                        _: 'center',
                                        md: 'start',
                                    }}
                                >
                                    {badges_row_1.map((badgeImage) => (
                                        <Image img={badgeImage.img} />
                                    ))}
                                </Inline>
                            )}
                            {badges_row_2.length && (
                                <Inline
                                    gap={rem(8)}
                                    justifyContent={{
                                        _: 'center',
                                        md: 'start',
                                    }}
                                >
                                    {badges_row_2.map((badgeImage) => (
                                        <Image
                                            img={badgeImage.img}
                                            style={{
                                                width: 75,
                                                height: 'auto',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    ))}
                                </Inline>
                            )}
                        </Box>
                    </Box>
                </Grid>
            </Box>
            <Box
                backgroundColor={theme.colors.primary[1700]}
                color={theme.colors.white}
                paddingBottom={rem(20)}
                paddingTop={{ _: rem(36), lg: rem(20) }}
                display="flex"
                justifyContent={{ _: 'center', lg: 'space-between' }}
                flexDirection={{ _: 'column', lg: 'row' }}
                textAlign="center"
                css={css`
                    gap: ${rem(16)};
                `}
            >
                <Grid>
                    <Box
                        gridColumn="1/-1"
                        display="flex"
                        alignItems="center"
                        css={css`
                            @media screen and (max-width: calc(${({ theme }) =>
                                    rem(stripUnit(theme.breakpoints.lg))})) {
                                flex-direction: column;
                            }
                        `}
                        gap={rem(32)}
                    >
                        <Box
                            order={{ _: 3, md: 0 }}
                            as={Caption}
                            color={theme.colors.white}
                            css={css`
                                @media screen and (max-width: calc(${({
                                        theme,
                                    }) =>
                                        rem(
                                            stripUnit(theme.breakpoints.lg)
                                        )})) {
                                }
                            `}
                        >
                            {copyright}
                        </Box>

                        <Box ml="auto" />
                        {ksa_button_link.url && (
                            <Box display={{ _: 'none', lg: 'flex' }}>
                                <PrismicLink link={ksa_button_link}>
                                    <Button
                                        startIcon={<KsaFlag />}
                                        isLight
                                        variant="tertiary"
                                    >
                                        {ksa_button_text}
                                    </Button>
                                </PrismicLink>
                            </Box>
                        )}

                        {cta_1_image.url && cta_2_image.url && (
                            <Box
                                display="flex"
                                gap={{ _: rem(16), lg: rem(8) }}
                                gridColumn={{ _: '1', lg: '1' }}
                                justifyContent={{
                                    _: 'center',
                                    md: 'start',
                                    lg: 'start',
                                }}
                            >
                                <PrismicLink link={cta_1_link}>
                                    <Box
                                        as={Image}
                                        width={rem(144)}
                                        img={cta_1_image}
                                        alt={cta_1_image.alt}
                                        _hover={{ opacity: 0.75 }}
                                    />
                                </PrismicLink>
                                <PrismicLink link={cta_2_link}>
                                    <Box
                                        as={Image}
                                        width={rem(144)}
                                        img={cta_2_image}
                                        alt={cta_2_image.alt}
                                        _hover={{ opacity: 0.75 }}
                                    />
                                </PrismicLink>
                            </Box>
                        )}
                        <Box
                            display="grid"
                            justifyContent="center"
                            alignItems="center"
                            gridAutoFlow="column"
                            gridGap={{ _: rem(64), lg: rem(8) }}
                            css={css`
                                @media screen and (max-width: calc(${({
                                        theme,
                                    }) =>
                                        rem(
                                            stripUnit(theme.breakpoints.lg)
                                        )})) {
                                }
                            `}
                        >
                            {social_links.map(({ icon, link }) => (
                                <PrismicLink
                                    link={link}
                                    width={rem(32)}
                                    height={rem(32)}
                                    css={css`
                                        a {
                                            display: inline-grid;
                                            place-items: center;
                                            border-radius: ${theme.radii.sm};
                                            &:hover {
                                                background-color: ${({
                                                    theme,
                                                }) =>
                                                    theme.colors.primary[1400]};
                                            }
                                        }
                                    `}
                                >
                                    <Box
                                        width={rem(24)}
                                        height={rem(24)}
                                        as={Image}
                                        img={icon}
                                        alt={icon.alt}
                                    />
                                </PrismicLink>
                            ))}
                        </Box>
                    </Box>
                </Grid>
            </Box>
        </Box>
    );
};

export default Footer;
